<template>
  <v-sheet class="border-opacity-25 border-thin rounded-lg pa-5">
    <v-form ref="form" @submit.prevent="onSubmit">
      <v-row>
        <v-col cols="4" md="4">
          <v-text-field
            v-model="criteria.number"
            label="Criteria #"
            type="number"
            :rules="[rules.required]" />
        </v-col>
        <v-col cols="4" md="4">
          <v-text-field v-model="criteria.name" label="Name" :rules="[rules.required]" />
        </v-col>
        <v-col cols="4" md="4">
          <v-select
            v-model="selectedGroupId"
            label="Criteria Group"
            :items="criteriasGroups"
            item-title="group_name"
            item-value="id"
            :rules="[rules.required]" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="12">
          <Editor
            v-model="criteria.description"
            :init="{
              base_url: '/tinymce',
              suffix: '.min',
              plugins: 'lists link image table code',
              toolbar:
                'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
              skin: 'oxide',
              content_css: 'default',
            }"
            licenseKey="gpl"></Editor>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field v-model="criteria.max_count" label="Default Max" type="number" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-expansion-panels v-for="(byc, index) in criteria.branchYearCriterias" :key="index">
            <v-expansion-panel>
              <v-row>
                <v-col>
                  {{ byc.branchYear?.branch?.name }}
                </v-col>
                <v-col>
                  <v-text-field v-model="byc.max_count" label="Max" type="number" />
                </v-col>
              </v-row>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            :loading="isSubmitting"
            color="secondary"
            block
            class="rounded-lg mb-8"
            elevation="2"
            @click="emit('criteriaEditCanceled')">
            Cancel
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            type="submit"
            :loading="isSubmitting"
            block
            class="rounded-lg mb-8"
            elevation="2"
            color="primary"
            >{{ saveLabel }}</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
  </v-sheet>
</template>
<script lang="ts" setup>
import { BranchDTO, CriteriaBranchYearDTO, CriteriaDTO, ICriteriaDTO } from '@/dtos';
import { computed, ref, toRef, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import { rules } from '@/helpers';
import { useCriteriaStore, useUpdateStatusStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';
import { getNextCriteriaNumber } from '@/controllers/admin';
import { useBranchYearStore } from '@/stores/modules/admin';
import { BranchYearDTO } from '../../dtos/branch.year.dto';
import Editor from '@tinymce/tinymce-vue';
import '@/components/tinymce/tinymce';

interface Props {
  criteria: ICriteriaDTO;
  yearId: string;
  isCreate: boolean;
}
const criteriaStore = useCriteriaStore();
const statusStore = useUpdateStatusStore();
const branchYearStore = useBranchYearStore();
const props = defineProps<Props>();
const emit = defineEmits(['criteriaEditComplete', 'criteriaEditCanceled', 'snack']);

const criteria = ref<ICriteriaDTO>(new CriteriaDTO(props.criteria));
const isCreate = toRef(props, 'isCreate', false);
const yearId = toRef(props, 'yearId', '');
const form = ref<InstanceType<typeof VForm> | null>();
const isSubmitting = ref<boolean>(false);
const query = branchYearStore.getBranchesForYearQuery(ref(yearId));
const availableBranches = computed(() => query.data.value?.data);
const { data } = criteriaStore.getCriteriaGroupsForYearQuery(yearId);
const criteriasGroups = computed(() => {
  return data.value?.data;
});

const selectedGroupId = computed({
  get: () => criteria.value.group?.id,
  set: (id) => {
    criteria.value.group = criteriasGroups.value?.find((group) => group.id === id) || undefined;
  },
});

const saveLabel = computed(() => {
  if (isCreate.value == true) {
    return 'Create Criteria';
  } else {
    return 'Save';
  }
});

const setCriteriaBranchYear = (crit: ICriteriaDTO) => {
  if (crit.branchYearCriterias.length == 0 && availableBranches.value) {
    for (const branch of availableBranches.value) {
      let byc = new CriteriaBranchYearDTO();
      byc.max_count = criteria.value.max_count;
      byc.branchYear = new BranchYearDTO(
        branch.branchYears.find((by) => by.year?.id === yearId.value),
      );
      if (byc.branchYear) {
        byc.branchYear.branch = new BranchDTO(branch);
        byc.criteria = new CriteriaDTO(criteria.value);
      }
      crit.branchYearCriterias.push(byc);
    }
  }
};

watch(
  () => availableBranches.value,
  (branches) => {
    if (branches) {
      setCriteriaBranchYear(criteria.value);
    }
  },
  { immediate: true },
);

watch(
  criteria.value,
  async (value: ICriteriaDTO) => {
    if (isCreate.value && value.number === 0) {
      value.number = await getNextCriteriaNumber(yearId.value);
    }
  },
  { immediate: true },
);

const onSubmit = async () => {
  if (!form.value) {
    return;
  }
  isSubmitting.value = true;
  if (isCreate.value) {
    criteriaStore.createCriteria.mutateAsync({ yearId: yearId.value, criteria: criteria.value });
  } else {
    criteriaStore.updateCriteria.mutateAsync(criteria.value);
  }
};
const finalizeSubmission = () => {
  isSubmitting.value = false;
  emit('criteriaEditComplete');
};

watch(
  () => statusStore.getStatus(`criteria-${criteria.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `criteria-${criteria.value.id}`;
    emit('snack', 'Criteria', value, statusStore.getError(key));
    finalizeSubmission();
  },
);

watch(
  () => statusStore.getStatus(`criteria-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `criteria-create`;
    emit('snack', 'Criteria', value, statusStore.getError(key));
    finalizeSubmission();
  },
);
</script>
<!--
Split Criteria Max into Branch
-->
